import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createUser } from "../redux/actions/UserActions";

import {
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiCalendar,
  FiBriefcase,
  FiPlus,
  FiArrowLeft,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const AddUser = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    dob: "",
    doj: "",
  });

  const [formError, setFormError] = useState("");
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { creating, error, success } = useSelector((state) => state.User);


  useEffect(() => {
    if (success) {
      // Reset form on success
      setFormData({
        name: "",
        email: "",
        phone: "",
        password: "",
        dob: "",
        doj: "",
      });
      setTouched({});
      // Optionally navigate back or show success message
      setTimeout(() => {
        navigate("/dashboard/employee");
      }, 2000);
    }
  }, [success, navigate]);
 
  const handleChange = (e) => {
    console.log(e);
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: [].includes(name)
        ? Number(value)
        : value,
    }));

    // Clear error when user starts typing
    if (formError && name === "phone") {
      setFormError("");
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  const validateForm = () => {
    // Phone validation
    if (!/^\d{10}$/.test(formData.phone)) {
      setFormError("Phone number must be exactly 10 digits");
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError("Please enter a valid email address");
      return false;
    }

    // Password validation
    if (formData.password.length < 6) {
      setFormError("Password must be at least 6 characters long");
      return false;
    }

    // Date validation
    const today = new Date().toISOString().split("T")[0];
    if (formData.doj > today) {
      setFormError("Date of Joining cannot be in the future");
      return false;
    }
   

    setFormError("");
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

   
    const payload = {
      ...formData,
      
      role: "employee", // hardcoded as per your Postman
    };

    

    dispatch(createUser(payload));
  };

  const getFieldError = (fieldName) => {
    if (!touched[fieldName]) return null;

    switch (fieldName) {
      case "phone":
        return !/^\d{10}$/.test(formData.phone) ? "Must be 10 digits" : null;
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(formData.email) ? "Invalid email format" : null;
      case "password":
        return formData.password.length < 6 ? "At least 6 characters" : null;
      default:
        return null;
    }
  };

  const inputFields = [
    {
      label: "Full Name",
      name: "name",
      type: "text",
      icon: FiUser,
      placeholder: "Enter full name",
      required: true,
    },
    {
      label: "Email Address",
      name: "email",
      type: "email",
      icon: FiMail,
      placeholder: "Enter email address",
      required: true,
    },
    {
      label: "Phone Number",
      name: "phone",
      type: "tel",
      icon: FiPhone,
      placeholder: "Enter 10-digit phone number",
      required: true,
    },
    {
      label: "Password",
      name: "password",
      type: showPassword ? "text" : "password",
      icon: FiLock,
      placeholder: "Enter password (min. 6 characters)",
      required: true,
    },
    {
      label: "Date of Birth",
      name: "dob",
      type: "date",
      icon: FiCalendar,
      required: true,
    },
    {
      label: "Date of Joining",
      name: "doj",
      type: "date",
      icon: FiCalendar,
      required: true,
    },
  ];

  // Fix error message display
  const getErrorMessage = () => {
    if (formError) return formError;
    if (typeof error === "string") return error;
    if (error?.message) return error.message;
    return "Something went wrong";
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/dashboard/employee")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="text-lg" />
            Back to Employees
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <FiUser className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  Add New User
                </h1>
                <p className="text-blue-100">
                  Create a new employee account in the system
                </p>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6">
            {/* Success Message */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <FiPlus className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-green-800 dark:text-green-300 font-medium">
                      User created successfully!
                    </p>
                    <p className="text-green-600 dark:text-green-400 text-sm">
                      Redirecting to employee list...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {(formError || error) && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <FiPlus className="text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-red-800 dark:text-red-300 font-medium">
                      {getErrorMessage()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              autoComplete="off"
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {inputFields.map(
                  ({
                    label,
                    name,
                    type,
                    icon: Icon,
                    placeholder,
                    required,
                  }) => (
                    <div key={name} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {label}{" "}
                        {required && <span className="text-red-500">*</span>}
                      </label>

                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Icon className="h-5 w-5 text-gray-400" />
                        </div>

                        {name === "password" ? (
                          <div className="relative">
                            <input
                              type={type}
                              name={name}
                              value={formData[name]}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={required}
                              placeholder={placeholder}
                              className={`w-full pl-10 pr-10 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                                getFieldError(name) ? "ring-2 ring-red-500" : ""
                              }`}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-sm"
                            >
                              {showPassword ? "Hide" : "Show"}
                            </button>
                          </div>
                        ) : (
                          <input
                            type={type}
                            name={name}
                            value={formData[name]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required={required}
                            placeholder={placeholder}
                            className={`w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                              getFieldError(name) ? "ring-2 ring-red-500" : ""
                            }`}
                          />
                        )}
                      </div>

                      {getFieldError(name) && (
                        <p className="text-red-500 text-xs mt-1">
                          {getFieldError(name)}
                        </p>
                      )}
                    </div>
                  )
                )}

          
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={creating}
                  className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl font-semibold text-white transition-all ${
                    creating
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  }`}
                >
                  {creating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating User...
                    </>
                  ) : (
                    <>
                      <FiPlus className="text-lg" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Form Tips */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            📝 Form Guidelines
          </h3>
          <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-2">
            <li>• Phone number must be exactly 10 digits</li>
            <li>• Password should be at least 6 characters long</li>
            <li>• Use a valid email address format</li>
            <li>• Date of Joining cannot be in the future</li>
            <li>• All fields marked with * are required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
